<script setup lang="ts">
import { digitsOnly as vDigitsOnly } from '@/common/directives/digitsOnly'
import type { ExternalCourseLevel } from '@/models/current-student-advanced-standing-form/api'

defineProps<{
  externalCourseLevelId: string
  courseLevelList: ExternalCourseLevel[]
  externalCourseName: string
  externalCourseCode: string
  externalCourseCompletedYear: string
}>()

const emit = defineEmits<{
  'update:externalCourseLevelId': [value: string]
  'update:externalCourseName': [value: string]
  'update:externalCourseCode': [value: string]
  'update:externalCourseCompletedYear': [value: string]
}>()

function onExternalCourseNameInput(event: Event): void {
  emit('update:externalCourseName', (event.target as HTMLInputElement).value)
}

function onExternalCourseCodeInput(event: Event): void {
  emit('update:externalCourseCode', (event.target as HTMLInputElement).value)
}

function onExternalCourseCompletedYearInput(event: Event): void {
  emit('update:externalCourseCompletedYear', (event.target as HTMLInputElement).value)
}

function onCourseLevelChange(event: Event): void {
  emit('update:externalCourseLevelId', (event.target as HTMLSelectElement).value)
}
</script>

<template>
          <fieldset>
            <legend>Course Details</legend>

            <div class="form-field requiredinput">
              <label for="externalCourseLevelInput">What type of course did you study?</label>
              <select
                required
                id="externalCourseLevelInput"
                :value="externalCourseLevelId"
                class="form-select"
                data-admissions-name="external_course_level_id"
                data-admissions-group="CourseInfo"
                @change="onCourseLevelChange"
              >
                <option value="">Please Select</option>
                <option
                  v-for="courseLevel in courseLevelList"
                  :key="courseLevel.external_course_level_id"
                  :value="String(courseLevel.external_course_level_id)"
                >
                  {{ courseLevel.external_course_level_name }}
                </option>
              </select>
            </div>

            <div class="form-field requiredinput">
              <label for="externalCourseNameInput">What is the name of the course that you studied?</label>
              <input
                required
                id="externalCourseNameInput"
                :value="externalCourseName"
                class="form-control"
                data-admissions-name="external_course_name"
                data-admissions-group="CourseInfo"
                @input="onExternalCourseNameInput"
              >
            </div>

            <div class="form-field">
              <label for="externalCourseCodeInput">What is the qualification code of the course that you studied?</label>
              For example: BSB51413.
              <input
                id="externalCourseCodeInput"
                :value="externalCourseCode"
                class="form-control"
                data-admissions-name="external_course_code"
                data-admissions-group="CourseInfo"
                @input="onExternalCourseCodeInput"
              >
            </div>

            <div class="form-field">
              <label for="externalCourseYearInput">What year did you complete this course? (Leave blank if you did not complete the course)</label>
              <input
                id="externalCourseYearInput"
                v-digits-only
                :value="externalCourseCompletedYear"
                inputmode="numeric"
                pattern="[0-9][0-9][0-9][0-9]"
                class="form-control"
                data-admissions-name="external_course_completed_year"
                data-admissions-group="CourseInfo"
                @input="onExternalCourseCompletedYearInput"
              >
              <p>Note: Credit is unlikely to be granted for studies completed more than 8 years ago.</p>
            </div>

            <slot />
          </fieldset>
</template>
